import { useDispatch, useSelector } from "react-redux";
import {
  getUserCategories,
  getUserData,
  setCreateTestSettings,
  setTestMode,
  setUserCategories,
} from "../../store/login.slice";
import { useNavigate } from "react-router-dom";
import { setCreatedTest, setTestStart } from "../../store/test.slice";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useStartTestMutation } from "../../api/testSlice.ts/testSlice";
import useTokenSpoilCheck from "../../hooks/tokenspoilcheck";
import {
  useGrabCategoryCountsQuery,
  useUpdateCreateTestSettingsMutation,
  useUpdatetestmodeMutation,
} from "../../api/accountSlice/accountSlice";
import type { CreateTestSettings } from "../../interfaces/logindata";
import {
  Check,
  CheckCircle,
  CircleDashed,
  Flag,
  GraduationCap,
  HelpCircle,
  PenSquare,
  Timer,
  XCircle,
} from "lucide-react";
import styles from "./createTest.module.scss";

const MAX_QUESTIONS = 50;
const DEFAULT_NUM_QUESTIONS = 10;
const SAVE_DEBOUNCE_MS = 600;

const DEFAULT_QUESTION_MODES = {
  Unused: true,
  Correct: false,
  Incorrect: false,
  Marked: false,
  Omitted: false,
};

type QuestionModes = {
  Unused: boolean;
  Correct: boolean;
  Incorrect: boolean;
  Marked: boolean;
  Omitted: boolean;
};

type QuestionModeKey = keyof QuestionModes;

type CategoryCountKey =
  | "TotalNumberUnusedInCategory"
  | "TotalNumberCorrectInCategory"
  | "TotalNumberInCorrectInCategory"
  | "TotalNumberMarkedInCategory"
  | "TotalNumberOmittedInCategory";

//
// Page specific components
//
type SectionProps = {
  title: string;
  subtext?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
};

function Section({ title, subtext, action, children }: SectionProps) {
  return (
    <div className={styles.createTestSectionContainer}>
      <h2 className={styles.createTestSectionHeader}>{title}</h2>
      {action}
      {subtext && <p className={styles.createTestSectionSubtext}>{subtext}</p>}
      <div className={styles.createTestSectionContentSpacer} />
      <div className={styles.createTestSectionOptionsContainer}>{children}</div>
    </div>
  );
}

type DetailedOptionProps = {
  icon: ReactNode;
  title: string;
  description: string;
  checked: boolean;
  count?: number;
  onClick: () => void;
};

function DetailedOption({
  icon,
  title,
  description,
  checked,
  count,
  onClick,
}: DetailedOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className={`${styles.createTestSectionOption} ${
        checked ? styles.selectedOption : ""
      }`}
    >
      <div
        className={`${styles.createTestButtonCheckboxWrapper} ${
          checked ? styles.checkboxWrapperChecked : ""
        }`}
      >
        <div className={styles.createTestSectionOptionCheckboxContainer}>
          {checked && <Check className={styles.createTestButtonCheckIcon} />}
        </div>
      </div>
      <div className={styles.createTestButtonContentWrapper}>
        {icon}
        <h2 className={styles.createTestButtonName}>
          {title} {count !== undefined ? `(${count})` : ""}
        </h2>
        <p className={styles.createTestButtonDescription}>{description}</p>
      </div>
    </button>
  );
}

type SimpleOptionProps = {
  label: string;
  count: number;
  checked: boolean;
  onClick: () => void;
};

function SimpleOption({ label, count, checked, onClick }: SimpleOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className={`${styles.createTestSectionSimpleOption} ${
        checked ? styles.selectedOption : ""
      }`}
    >
      <div
        className={`${styles.createTestSectionOptionCheckboxContainer} ${
          checked ? styles.simpleCheckbox : ""
        }`}
      >
        {checked && (
          <Check
            className={`${styles.createTestButtonCheckIcon} ${styles.simpleCheckIcon}`}
          />
        )}
      </div>
      {`${label} (${count})`}
    </button>
  );
}

const QUESTION_FILTERS: {
  key: QuestionModeKey;
  icon: ReactNode;
  description: string;
  categoryKey: CategoryCountKey;
}[] = [
  {
    key: "Unused",
    icon: <HelpCircle className={styles.createTestButtonIcon} />,
    description: "Include questions that you have not yet answered",
    categoryKey: "TotalNumberUnusedInCategory",
  },
  {
    key: "Correct",
    icon: <CheckCircle className={styles.createTestButtonIcon} />,
    description:
      "Include questions that you have previously answered correctly",
    categoryKey: "TotalNumberCorrectInCategory",
  },
  {
    key: "Incorrect",
    icon: <XCircle className={styles.createTestButtonIcon} />,
    description:
      "Include questions that you have previously answered incorrectly",
    categoryKey: "TotalNumberInCorrectInCategory",
  },
  {
    key: "Marked",
    icon: <Flag className={styles.createTestButtonIcon} />,
    description: "Include questions that you have previously marked",
    categoryKey: "TotalNumberMarkedInCategory",
  },
  {
    key: "Omitted",
    icon: <CircleDashed className={styles.createTestButtonIcon} />,
    description:
      "Include questions that you have previously seen but not answered",
    categoryKey: "TotalNumberOmittedInCategory",
  },
];

function CreateTest() {
  //
  //Selectors
  //
  const categories = useSelector(getUserCategories);
  const userData = useSelector(getUserData);
  const dispatch = useDispatch();
  //
  //local state
  //
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  const [errorState, setErrorState] = useState(false);
  const [warningState, setWarningState] = useState("");
  //Tutor and Timed are independent toggles (both, either or neither)
  const [testModes, setTestModes] = useState({
    tutor: userData.tutor_mode === true,
    timed: userData.timed_mode !== false,
  });
  //Last saved Create Test selections (null until the user changes something)
  const savedSettings: CreateTestSettings | null | undefined =
    userData.create_test_settings;
  const [questionModesData, setQuestionModesData] = useState<QuestionModes>(
    () => ({
      ...DEFAULT_QUESTION_MODES,
      ...(savedSettings?.question_modes ?? {}),
    }),
  );
  const defaultCategories = categories.map((element) => {
    return element.Category;
  });
  //Saved as excluded subjects, so categories added later start selected
  const [subjectsData, setSubjectsData] = useState(() => {
    const excluded = savedSettings?.excluded_subjects ?? [];
    return defaultCategories.filter((c) => !excluded.includes(c));
  });
  const [numberOfQuestions, setNumberOfQuestions] = useState<
    number | string | undefined
  >(() => {
    const saved = Number(savedSettings?.num_questions);
    return Number.isInteger(saved) && saved >= 1 && saved <= MAX_QUESTIONS
      ? saved
      : DEFAULT_NUM_QUESTIONS;
  });
  //
  //Queries
  //
  const [startTest, { isLoading }] = useStartTestMutation();
  const [updatetestmode] = useUpdatetestmodeMutation();
  const [updateCreateTestSettings] = useUpdateCreateTestSettingsMutation();
  const { data } = useGrabCategoryCountsQuery({});
  useEffect(() => {
    if (data) {
      dispatch(setUserCategories({ categories: [...data] }));
    }
  }, [data, dispatch]);
  //
  //Hooks
  //
  useTokenSpoilCheck(errorStatusCode);
  const navigate = useNavigate();

  //Saved as the account default only when the user changes it (not on every visit)
  const toggleTestMode = (mode: "tutor" | "timed") => {
    const next = { ...testModes, [mode]: !testModes[mode] };
    setTestModes(next);
    const saved = { tutor_mode: next.tutor, timed_mode: next.timed };
    dispatch(setTestMode(saved));
    updatetestmode(saved);
  };

  //Save question modes / number of questions / subjects whenever they change.
  //Debounced so typing in the number box doesn't send a request per keystroke.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; //don't save on page load, only on a real change
    }
    //Wait until categories have loaded, or every subject would look excluded
    if (categories.length === 0) return;

    const num = Number(numberOfQuestions);
    const settings: CreateTestSettings = {
      question_modes: questionModesData,
      excluded_subjects: defaultCategories.filter(
        (c) => !subjectsData.includes(c),
      ),
    };
    //Only keep a valid count; an empty/out-of-range box keeps the last good value
    if (Number.isInteger(num) && num >= 1 && num <= MAX_QUESTIONS) {
      settings.num_questions = num;
    } else if (savedSettings?.num_questions != null) {
      settings.num_questions = savedSettings.num_questions;
    }

    const timer = setTimeout(() => {
      dispatch(setCreateTestSettings(settings));
      updateCreateTestSettings({ settings });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionModesData, numberOfQuestions, subjectsData]);

  //
  //Derived data
  //
  // Total questions per mode, summed across the selected subjects only
  const questionModeTotals = useMemo(() => {
    const selected = categories.filter((category) =>
      subjectsData.includes(category.Category),
    );
    const totals = {} as Record<QuestionModeKey, number>;
    QUESTION_FILTERS.forEach(({ key, categoryKey }) => {
      totals[key] = selected.reduce(
        (sum, category) => sum + (Number(category[categoryKey]) || 0),
        0,
      );
    });
    return totals;
  }, [categories, subjectsData]);

  //
  //Util funcs
  //
  function getQuestionCountByCategoryKey(key: string) {
    const category = categories.find((element) => element.Category === key);
    if (!category) return 0;

    return QUESTION_FILTERS.reduce(
      (total, { key: modeKey, categoryKey }) =>
        questionModesData[modeKey]
          ? total + (Number(category[categoryKey]) || 0)
          : total,
      0,
    );
  }

  const toggleSubject = (category) => {
    setSubjectsData((prev) =>
      prev.includes(category)
        ? prev.filter((element) => element !== category)
        : [...prev, category],
    );
  };

  const allSelected =
    categories.length > 0 && subjectsData.length === categories.length;

  const handleGenerateTest = async () => {
    setErrorState(false);
    setWarningState("");

    if (subjectsData.length == 0) {
      setWarningState(
        "You must select at least 1 subject before creating a new test.",
      );
      return;
    }

    let subjectTotalCount = 0;
    subjectsData.forEach((element) => {
      subjectTotalCount += getQuestionCountByCategoryKey(element);
    });

    if (subjectTotalCount == 0) {
      setWarningState("The selected subject(s) must have at least 1 question.");
      return;
    }

    if (subjectTotalCount < Number(numberOfQuestions)) {
      setWarningState("The selected subject(s) do not have enough questions!");
      return;
    }

    if (!numberOfQuestions || Number(numberOfQuestions) <= 0) {
      setWarningState("Each test must have at least 1 question in the block.");
      return;
    }

    if (Number(numberOfQuestions) > MAX_QUESTIONS) {
      setWarningState(`Each test can have at most ${MAX_QUESTIONS} questions.`);
      return;
    }

    const testObject = {
      TutorMode: testModes.tutor,
      TimedMode: testModes.timed,
      Subjects: subjectsData,
      Unused: questionModesData.Unused,
      Correct: questionModesData.Correct,
      Incorrect: questionModesData.Incorrect,
      Marked: questionModesData.Marked,
      Omitted: questionModesData.Omitted,
      NumQuestions: Number(numberOfQuestions),
    };

    try {
      //Generate some sort of test
      const result = await startTest({
        testStartData: testObject,
      }).unwrap();
      dispatch(
        setCreatedTest({
          createdTest: {
            QuestionsForTest: result.QuestionsForTest,
            FirstQuestion: result.FirstQuestion,
            AnswerChoicesForTest: result?.AnswerChoicesForTest,
            guid: result.guid,
            tutorMode: testModes.tutor,
            timedMode: testModes.timed,
          },
        }),
      );
    } catch (error) {
      setErrorState(true);
      console.error("Error occurred during test creation:", error);
      setErrorStatusCode(error?.status);
      return;
    }

    //Set an end point for the test for now for now
    dispatch(
      setTestStart({
        testStartDate: Date.now(),
      }),
    );

    //Navigate to the test portion
    navigate("/test");
  };

  return (
    <div className={styles.homepagePageContainer}>
      <div className={styles.createTestContainer}>
        <div className={styles.homepageTitleContainer}>
          <PenSquare className={styles.homepageTitleIcon} />
          <h1 className={styles.homepageTitleText}>Create Test</h1>
        </div>

        <Section
          title="Test Mode"
          subtext="Turn on either mode, both, or neither. Tutor mode grades each question right after you answer it. Timed mode gives you a countdown."
        >
          <DetailedOption
            icon={<GraduationCap />}
            title="Tutor"
            description="See the answer and explanation right after you answer each question"
            checked={testModes.tutor}
            onClick={() => toggleTestMode("tutor")}
          />
          <DetailedOption
            icon={<Timer />}
            title="Timed"
            description="Answer against the clock, 2 minutes per question. Turn off to study untimed"
            checked={testModes.timed}
            onClick={() => toggleTestMode("timed")}
          />
        </Section>

        <Section
          title="Question Mode"
          subtext="Filter the questions you would like to study."
        >
          {QUESTION_FILTERS.map(({ key, icon, description }) => (
            <DetailedOption
              key={key}
              icon={icon}
              title={key}
              description={description}
              count={questionModeTotals[key]}
              checked={questionModesData[key]}
              onClick={() =>
                setQuestionModesData((prev) => ({
                  ...prev,
                  [key]: !prev[key],
                }))
              }
            />
          ))}
        </Section>

        <Section
          title="No. of Questions"
          subtext={`The maximum questions per block is ${MAX_QUESTIONS}`}
        >
          <input
            type="number"
            id="numberInput"
            className={styles.createTestInput}
            placeholder="0"
            min={0}
            max={MAX_QUESTIONS}
            value={numberOfQuestions}
            onChange={(e) => {
              setNumberOfQuestions(e.target.value);
            }}
          />
        </Section>

        <Section
          title="Subjects"
          action={
            <p
              className={styles.createTestSectionSelectAll}
              tabIndex={0}
              onClick={() =>
                allSelected
                  ? setSubjectsData([])
                  : setSubjectsData(defaultCategories)
              }
            >
              {allSelected ? "Deselect All" : "Select All"}
            </p>
          }
        >
          {categories.map((item, index) => (
            <SimpleOption
              key={`${index}-${item.Category}`}
              label={item.Category}
              count={getQuestionCountByCategoryKey(item.Category)}
              checked={subjectsData.includes(item.Category)}
              onClick={() => toggleSubject(item.Category)}
            />
          ))}
        </Section>
      </div>

      <div className={styles.homepageFooterContainer}>
        {(errorState || warningState) && (
          <span className={styles.homepageFooterMessage}>
            {errorState
              ? "Something went wrong, please try again"
              : warningState}
          </span>
        )}
        {isLoading ? (
          <div className={styles.homepageFooterSpinnerWrapper}>
            <div className={styles.homepageFooterSpinner} />
          </div>
        ) : (
          <button
            type="button"
            className={`${styles.homepageFooterButton} ${styles.primaryButton}`}
            onClick={handleGenerateTest}
          >
            Begin Test
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateTest;
